<template>
  <v-app theme="dark">
    <DefaultBar />

    <slot />

    <DefaultFooter />
  </v-app>
</template>

<script setup lang="ts">
import DefaultBar from "@/layouts/default/AppBar.vue";
import DefaultFooter from "@/layouts/default/Footer.vue";
</script>

<style>
:root {
  color-scheme: dark;
}
:root {
  --v-theme-background: 18, 18, 18;
  --v-theme-background-overlay-multiplier: 1;
  --v-theme-surface: 33, 33, 33;
  --v-theme-surface-overlay-multiplier: 1;
  --v-theme-surface-bright: 204, 191, 214;
  --v-theme-surface-bright-overlay-multiplier: 2;
  --v-theme-surface-light: 66, 66, 66;
  --v-theme-surface-light-overlay-multiplier: 1;
  --v-theme-surface-variant: 163, 163, 163;
  --v-theme-surface-variant-overlay-multiplier: 2;
  --v-theme-on-surface-variant: 66, 66, 66;
  --v-theme-primary: 33, 150, 243;
  --v-theme-primary-overlay-multiplier: 2;
  --v-theme-primary-darken-1: 39, 124, 193;
  --v-theme-primary-darken-1-overlay-multiplier: 2;
  --v-theme-secondary: 84, 182, 178;
  --v-theme-secondary-overlay-multiplier: 2;
  --v-theme-secondary-darken-1: 72, 169, 166;
  --v-theme-secondary-darken-1-overlay-multiplier: 2;
  --v-theme-error: 207, 102, 121;
  --v-theme-error-overlay-multiplier: 2;
  --v-theme-info: 33, 150, 243;
  --v-theme-info-overlay-multiplier: 2;
  --v-theme-success: 76, 175, 80;
  --v-theme-success-overlay-multiplier: 2;
  --v-theme-warning: 251, 140, 0;
  --v-theme-warning-overlay-multiplier: 2;
  --v-theme-on-background: 255, 255, 255;
  --v-theme-on-surface: 255, 255, 255;
  --v-theme-on-surface-bright: 0, 0, 0;
  --v-theme-on-surface-light: 255, 255, 255;
  --v-theme-on-primary: 255, 255, 255;
  --v-theme-on-primary-darken-1: 255, 255, 255;
  --v-theme-on-secondary: 255, 255, 255;
  --v-theme-on-secondary-darken-1: 255, 255, 255;
  --v-theme-on-error: 255, 255, 255;
  --v-theme-on-info: 255, 255, 255;
  --v-theme-on-success: 255, 255, 255;
  --v-theme-on-warning: 255, 255, 255;
  --v-border-color: 255, 255, 255;
  --v-border-opacity: 0.12;
  --v-high-emphasis-opacity: 1;
  --v-medium-emphasis-opacity: 0.7;
  --v-disabled-opacity: 0.5;
  --v-idle-opacity: 0.1;
  --v-hover-opacity: 0.04;
  --v-focus-opacity: 0.12;
  --v-selected-opacity: 0.08;
  --v-activated-opacity: 0.12;
  --v-pressed-opacity: 0.16;
  --v-dragged-opacity: 0.08;
  --v-theme-kbd: 33, 37, 41;
  --v-theme-on-kbd: 255, 255, 255;
  --v-theme-code: 52, 52, 52;
  --v-theme-on-code: 204, 204, 204;
}
.v-theme--light {
  color-scheme: normal;
  --v-theme-background: 255, 255, 255;
  --v-theme-background-overlay-multiplier: 1;
  --v-theme-surface: 255, 255, 255;
  --v-theme-surface-overlay-multiplier: 1;
  --v-theme-surface-bright: 255, 255, 255;
  --v-theme-surface-bright-overlay-multiplier: 1;
  --v-theme-surface-light: 238, 238, 238;
  --v-theme-surface-light-overlay-multiplier: 1;
  --v-theme-surface-variant: 66, 66, 66;
  --v-theme-surface-variant-overlay-multiplier: 2;
  --v-theme-on-surface-variant: 238, 238, 238;
  --v-theme-primary: 24, 103, 192;
  --v-theme-primary-overlay-multiplier: 2;
  --v-theme-primary-darken-1: 31, 85, 146;
  --v-theme-primary-darken-1-overlay-multiplier: 2;
  --v-theme-secondary: 72, 169, 166;
  --v-theme-secondary-overlay-multiplier: 1;
  --v-theme-secondary-darken-1: 1, 135, 134;
  --v-theme-secondary-darken-1-overlay-multiplier: 1;
  --v-theme-error: 176, 0, 32;
  --v-theme-error-overlay-multiplier: 2;
  --v-theme-info: 33, 150, 243;
  --v-theme-info-overlay-multiplier: 1;
  --v-theme-success: 76, 175, 80;
  --v-theme-success-overlay-multiplier: 1;
  --v-theme-warning: 251, 140, 0;
  --v-theme-warning-overlay-multiplier: 1;
  --v-theme-on-background: 0, 0, 0;
  --v-theme-on-surface: 0, 0, 0;
  --v-theme-on-surface-bright: 0, 0, 0;
  --v-theme-on-surface-light: 0, 0, 0;
  --v-theme-on-primary: 255, 255, 255;
  --v-theme-on-primary-darken-1: 255, 255, 255;
  --v-theme-on-secondary: 255, 255, 255;
  --v-theme-on-secondary-darken-1: 255, 255, 255;
  --v-theme-on-error: 255, 255, 255;
  --v-theme-on-info: 255, 255, 255;
  --v-theme-on-success: 255, 255, 255;
  --v-theme-on-warning: 255, 255, 255;
  --v-border-color: 0, 0, 0;
  --v-border-opacity: 0.12;
  --v-high-emphasis-opacity: 0.87;
  --v-medium-emphasis-opacity: 0.6;
  --v-disabled-opacity: 0.38;
  --v-idle-opacity: 0.04;
  --v-hover-opacity: 0.04;
  --v-focus-opacity: 0.12;
  --v-selected-opacity: 0.08;
  --v-activated-opacity: 0.12;
  --v-pressed-opacity: 0.12;
  --v-dragged-opacity: 0.08;
  --v-theme-kbd: 33, 37, 41;
  --v-theme-on-kbd: 255, 255, 255;
  --v-theme-code: 245, 245, 245;
  --v-theme-on-code: 0, 0, 0;
}
.v-theme--dark {
  color-scheme: dark;
  --v-theme-background: 18, 18, 18;
  --v-theme-background-overlay-multiplier: 1;
  --v-theme-surface: 33, 33, 33;
  --v-theme-surface-overlay-multiplier: 1;
  --v-theme-surface-bright: 204, 191, 214;
  --v-theme-surface-bright-overlay-multiplier: 2;
  --v-theme-surface-light: 66, 66, 66;
  --v-theme-surface-light-overlay-multiplier: 1;
  --v-theme-surface-variant: 163, 163, 163;
  --v-theme-surface-variant-overlay-multiplier: 2;
  --v-theme-on-surface-variant: 66, 66, 66;
  --v-theme-primary: 33, 150, 243;
  --v-theme-primary-overlay-multiplier: 2;
  --v-theme-primary-darken-1: 39, 124, 193;
  --v-theme-primary-darken-1-overlay-multiplier: 2;
  --v-theme-secondary: 84, 182, 178;
  --v-theme-secondary-overlay-multiplier: 2;
  --v-theme-secondary-darken-1: 72, 169, 166;
  --v-theme-secondary-darken-1-overlay-multiplier: 2;
  --v-theme-error: 207, 102, 121;
  --v-theme-error-overlay-multiplier: 2;
  --v-theme-info: 33, 150, 243;
  --v-theme-info-overlay-multiplier: 2;
  --v-theme-success: 76, 175, 80;
  --v-theme-success-overlay-multiplier: 2;
  --v-theme-warning: 251, 140, 0;
  --v-theme-warning-overlay-multiplier: 2;
  --v-theme-on-background: 255, 255, 255;
  --v-theme-on-surface: 255, 255, 255;
  --v-theme-on-surface-bright: 0, 0, 0;
  --v-theme-on-surface-light: 255, 255, 255;
  --v-theme-on-primary: 255, 255, 255;
  --v-theme-on-primary-darken-1: 255, 255, 255;
  --v-theme-on-secondary: 255, 255, 255;
  --v-theme-on-secondary-darken-1: 255, 255, 255;
  --v-theme-on-error: 255, 255, 255;
  --v-theme-on-info: 255, 255, 255;
  --v-theme-on-success: 255, 255, 255;
  --v-theme-on-warning: 255, 255, 255;
  --v-border-color: 255, 255, 255;
  --v-border-opacity: 0.12;
  --v-high-emphasis-opacity: 1;
  --v-medium-emphasis-opacity: 0.7;
  --v-disabled-opacity: 0.5;
  --v-idle-opacity: 0.1;
  --v-hover-opacity: 0.04;
  --v-focus-opacity: 0.12;
  --v-selected-opacity: 0.08;
  --v-activated-opacity: 0.12;
  --v-pressed-opacity: 0.16;
  --v-dragged-opacity: 0.08;
  --v-theme-kbd: 33, 37, 41;
  --v-theme-on-kbd: 255, 255, 255;
  --v-theme-code: 52, 52, 52;
  --v-theme-on-code: 204, 204, 204;
}
.bg-background {
  --v-theme-overlay-multiplier: var(--v-theme-background-overlay-multiplier);
  background-color: rgb(var(--v-theme-background)) !important;
  color: rgb(var(--v-theme-on-background)) !important;
}
.bg-surface {
  --v-theme-overlay-multiplier: var(--v-theme-surface-overlay-multiplier);
  background-color: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}
.bg-surface-bright {
  --v-theme-overlay-multiplier: var(
    --v-theme-surface-bright-overlay-multiplier
  );
  background-color: rgb(var(--v-theme-surface-bright)) !important;
  color: rgb(var(--v-theme-on-surface-bright)) !important;
}
.bg-surface-light {
  --v-theme-overlay-multiplier: var(--v-theme-surface-light-overlay-multiplier);
  background-color: rgb(var(--v-theme-surface-light)) !important;
  color: rgb(var(--v-theme-on-surface-light)) !important;
}
.bg-surface-variant {
  --v-theme-overlay-multiplier: var(
    --v-theme-surface-variant-overlay-multiplier
  );
  background-color: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface-variant)) !important;
}
.bg-primary {
  --v-theme-overlay-multiplier: var(--v-theme-primary-overlay-multiplier);
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}
.bg-primary-darken-1 {
  --v-theme-overlay-multiplier: var(
    --v-theme-primary-darken-1-overlay-multiplier
  );
  background-color: rgb(var(--v-theme-primary-darken-1)) !important;
  color: rgb(var(--v-theme-on-primary-darken-1)) !important;
}
.bg-secondary {
  --v-theme-overlay-multiplier: var(--v-theme-secondary-overlay-multiplier);
  background-color: rgb(var(--v-theme-secondary)) !important;
  color: rgb(var(--v-theme-on-secondary)) !important;
}
.bg-secondary-darken-1 {
  --v-theme-overlay-multiplier: var(
    --v-theme-secondary-darken-1-overlay-multiplier
  );
  background-color: rgb(var(--v-theme-secondary-darken-1)) !important;
  color: rgb(var(--v-theme-on-secondary-darken-1)) !important;
}
.bg-error {
  --v-theme-overlay-multiplier: var(--v-theme-error-overlay-multiplier);
  background-color: rgb(var(--v-theme-error)) !important;
  color: rgb(var(--v-theme-on-error)) !important;
}
.bg-info {
  --v-theme-overlay-multiplier: var(--v-theme-info-overlay-multiplier);
  background-color: rgb(var(--v-theme-info)) !important;
  color: rgb(var(--v-theme-on-info)) !important;
}
.bg-success {
  --v-theme-overlay-multiplier: var(--v-theme-success-overlay-multiplier);
  background-color: rgb(var(--v-theme-success)) !important;
  color: rgb(var(--v-theme-on-success)) !important;
}
.bg-warning {
  --v-theme-overlay-multiplier: var(--v-theme-warning-overlay-multiplier);
  background-color: rgb(var(--v-theme-warning)) !important;
  color: rgb(var(--v-theme-on-warning)) !important;
}
.text-background {
  color: rgb(var(--v-theme-background)) !important;
}
.border-background {
  --v-border-color: var(--v-theme-background);
}
.text-surface {
  color: rgb(var(--v-theme-surface)) !important;
}
.border-surface {
  --v-border-color: var(--v-theme-surface);
}
.text-surface-bright {
  color: rgb(var(--v-theme-surface-bright)) !important;
}
.border-surface-bright {
  --v-border-color: var(--v-theme-surface-bright);
}
.text-surface-light {
  color: rgb(var(--v-theme-surface-light)) !important;
}
.border-surface-light {
  --v-border-color: var(--v-theme-surface-light);
}
.text-surface-variant {
  color: rgb(var(--v-theme-surface-variant)) !important;
}
.border-surface-variant {
  --v-border-color: var(--v-theme-surface-variant);
}
.on-surface-variant {
  color: rgb(var(--v-theme-on-surface-variant)) !important;
}
.text-primary {
  color: rgb(var(--v-theme-primary)) !important;
}
.border-primary {
  --v-border-color: var(--v-theme-primary);
}
.text-primary-darken-1 {
  color: rgb(var(--v-theme-primary-darken-1)) !important;
}
.border-primary-darken-1 {
  --v-border-color: var(--v-theme-primary-darken-1);
}
.text-secondary {
  color: rgb(var(--v-theme-secondary)) !important;
}
.border-secondary {
  --v-border-color: var(--v-theme-secondary);
}
.text-secondary-darken-1 {
  color: rgb(var(--v-theme-secondary-darken-1)) !important;
}
.border-secondary-darken-1 {
  --v-border-color: var(--v-theme-secondary-darken-1);
}
.text-error {
  color: rgb(var(--v-theme-error)) !important;
}
.border-error {
  --v-border-color: var(--v-theme-error);
}
.text-info {
  color: rgb(var(--v-theme-info)) !important;
}
.border-info {
  --v-border-color: var(--v-theme-info);
}
.text-success {
  color: rgb(var(--v-theme-success)) !important;
}
.border-success {
  --v-border-color: var(--v-theme-success);
}
.text-warning {
  color: rgb(var(--v-theme-warning)) !important;
}
.border-warning {
  --v-border-color: var(--v-theme-warning);
}
.on-background {
  color: rgb(var(--v-theme-on-background)) !important;
}
.on-surface {
  color: rgb(var(--v-theme-on-surface)) !important;
}
.on-surface-bright {
  color: rgb(var(--v-theme-on-surface-bright)) !important;
}
.on-surface-light {
  color: rgb(var(--v-theme-on-surface-light)) !important;
}
.on-primary {
  color: rgb(var(--v-theme-on-primary)) !important;
}
.on-primary-darken-1 {
  color: rgb(var(--v-theme-on-primary-darken-1)) !important;
}
.on-secondary {
  color: rgb(var(--v-theme-on-secondary)) !important;
}
.on-secondary-darken-1 {
  color: rgb(var(--v-theme-on-secondary-darken-1)) !important;
}
.on-error {
  color: rgb(var(--v-theme-on-error)) !important;
}
.on-info {
  color: rgb(var(--v-theme-on-info)) !important;
}
.on-success {
  color: rgb(var(--v-theme-on-success)) !important;
}
.on-warning {
  color: rgb(var(--v-theme-on-warning)) !important;
}
</style>
